import { DateTime } from 'luxon';

import { YandexTaskInfo, YLog } from '@/types/yandex';

import {
  buildVariancePoint,
  EstimationVariancePoint,
  filterWorklogsByUser,
  isAssignedToUser,
  latestWorklog,
  resolveEstimateMinutes,
  sumWorklogMinutes,
  uniqueTaskKeysFromLogs,
  worklogsInPeriod,
} from './estimationVariance';

export type EstimationVarianceCache = {
  tasks: Map<string, YandexTaskInfo>;
  worklogs: Map<string, YLog[]>;
};

export function createEstimationVarianceCache(): EstimationVarianceCache {
  return {
    tasks: new Map(),
    worklogs: new Map(),
  };
}

export type EstimationVarianceDeps = {
  getTask: (params: { taskKey: string; signal?: AbortSignal }) => Promise<YandexTaskInfo>;
  getWorkLogs: (params: { taskKey: string; signal?: AbortSignal }) => Promise<YLog[]>;
};

export type LoadEstimationVarianceParams = {
  logs: Array<{ taskKey?: string }>;
  selectedTrackerUserId: string;
  periodStart: DateTime;
  periodEnd: DateTime;
  cache: EstimationVarianceCache;
  signal?: AbortSignal;
} & EstimationVarianceDeps;

export type LoadEstimationVarianceResult = {
  points: EstimationVariancePoint[];
  failedCount: number;
  skippedCount: number;
  skippedNoEstimateCount: number;
  skippedNoWorklogsCount: number;
};

type LoaderContext = EstimationVarianceDeps & {
  cache: EstimationVarianceCache;
  signal?: AbortSignal;
};

const FETCH_CONCURRENCY = 5;

function isAbortError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  if ('code' in error && (error as { code?: string }).code === 'ERR_CANCELED') {
    return true;
  }

  if ('name' in error && (error as { name?: string }).name === 'CanceledError') {
    return true;
  }

  return error instanceof DOMException && error.name === 'AbortError';
}

async function mapPool<T, R>({
  items,
  limit,
  mapper,
}: {
  items: T[];
  limit: number;
  mapper: (item: T) => Promise<R>;
}): Promise<R[]> {
  const results = new Array<R>(items.length);
  let nextIndex = 0;

  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (nextIndex < items.length) {
      const current = nextIndex;
      nextIndex += 1;
      results[current] = await mapper(items[current]);
    }
  });

  await Promise.all(workers);

  return results;
}

async function getCachedTask({
  taskKey,
  getTask,
  cache,
  signal,
}: LoaderContext & { taskKey: string }): Promise<YandexTaskInfo> {
  const cached = cache.tasks.get(taskKey);

  if (cached) {
    return cached;
  }

  const task = await getTask({ taskKey, signal });
  cache.tasks.set(taskKey, task);

  return task;
}

async function getCachedWorklogs({
  taskKey,
  getWorkLogs,
  cache,
  signal,
}: LoaderContext & { taskKey: string }): Promise<YLog[]> {
  const cached = cache.worklogs.get(taskKey);

  if (cached) {
    return cached;
  }

  const worklogs = await getWorkLogs({ taskKey, signal });
  cache.worklogs.set(taskKey, worklogs);

  return worklogs;
}

export async function loadEstimationVariance({
  logs,
  selectedTrackerUserId,
  periodStart,
  periodEnd,
  cache,
  signal,
  getTask,
  getWorkLogs,
}: LoadEstimationVarianceParams): Promise<LoadEstimationVarianceResult> {
  const taskKeys = uniqueTaskKeysFromLogs(logs);
  const points: EstimationVariancePoint[] = [];
  let failedCount = 0;
  let skippedNoEstimateCount = 0;
  let skippedNoWorklogsCount = 0;
  const ctx: LoaderContext = { getTask, getWorkLogs, cache, signal };

  await mapPool({
    items: taskKeys,
    limit: FETCH_CONCURRENCY,
    mapper: async (taskKey) => {
      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }

      let task: YandexTaskInfo;

      try {
        task = await getCachedTask({ ...ctx, taskKey });
      } catch (error) {
        if (isAbortError(error)) {
          throw error;
        }

        failedCount += 1;

        return;
      }

      if (!isAssignedToUser(task, selectedTrackerUserId)) {
        return;
      }

      const estimateMinutes = resolveEstimateMinutes(task);

      if (estimateMinutes <= 0) {
        skippedNoEstimateCount += 1;

        return;
      }

      let issueWorklogs: YLog[];

      try {
        issueWorklogs = await getCachedWorklogs({ ...ctx, taskKey });
      } catch (error) {
        if (isAbortError(error)) {
          throw error;
        }

        failedCount += 1;

        return;
      }

      const userWorklogs = filterWorklogsByUser(issueWorklogs, selectedTrackerUserId);

      if (userWorklogs.length === 0) {
        skippedNoWorklogsCount += 1;

        return;
      }

      const latestInPeriod = latestWorklog(worklogsInPeriod(userWorklogs, periodStart, periodEnd));

      if (!latestInPeriod) {
        return;
      }

      points.push(
        buildVariancePoint({
          task,
          spentMinutes: sumWorklogMinutes(userWorklogs),
          estimateMinutes,
          lastPeriodWorklogDate: latestInPeriod.start,
        }),
      );
    },
  });

  return {
    points: points.sort((a, b) => a.lastPeriodWorklogDate.localeCompare(b.lastPeriodWorklogDate)),
    failedCount,
    skippedCount: skippedNoEstimateCount + skippedNoWorklogsCount,
    skippedNoEstimateCount,
    skippedNoWorklogsCount,
  };
}
